import React, { useState, useEffect } from 'react';
import { makeApiRequest } from '../../../utils/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Button, Modal, Box, TextField, Typography, Alert, Grid, TablePagination, CircularProgress,
    Select, MenuItem, FormControl, InputLabel, InputAdornment, Chip, Tooltip
} from '@mui/material';
import { styled } from '@mui/system';
import { debounce } from 'lodash';
import { Search } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';


const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: theme.spacing(1),
    textAlign: 'center',
    '&:first-child': {
        width: '5%',
    },
}));

const StyledTableRow = styled(TableRow)(() => ({}));

const Module = () => {
    const [ModuleData, setModuleData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(null);
    const [editedModuleName, setEditedModuleName] = useState('');
    const [statusChangeModalOpen, setStatusChangeModalOpen] = useState(false);
    const [statusToUpdate, setStatusToUpdate] = useState('');
    const [status, setStatus] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentModuleName, setCurrentModuleName] = useState('');
    const [originalModuleName, setOriginalModuleName] = useState('');
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [totalItems, setTotalItems] = useState(0);
    const [currentItems, setCurrentItems] = useState([]);
    const [modules, setModules] = useState([]);
    const [selectedModule, setSelectedModule] = useState('');
    const [newModuleName, setNewModuleName] = useState('');
    const [allData, setAllData] = useState([]);

    const handleAddModule = async () => {
        if (!newModuleName) {
            toast.warn('Please fill in all fields');
            return;
        }

        setNewModuleName('');

        try {
            const response = await makeApiRequest('post', '/api/module/add', {
                token: localStorage.getItem('authToken'),
                module_name: newModuleName,
                userid: localStorage.getItem('userid'),
            });


            if (response.status === 'success') {
                fetchPaginatedData(page + 1, rowsPerPage);
                setSelectedModule('');
                toast.success('Module added successfully');
            } else {
                throw new Error('Failed to add chapter');
            }
        } catch (err) {
            console.error('API Error:', err.response.data?.message);
            toast.error(err.response.data?.message);
        }
    };

    const fetchPaginatedData = async (pageNumber, itemsPerPage) => {
        setLoading(true);
        try {
            const response = await makeApiRequest('post', '/api/module/list', {
                token: localStorage.getItem('authToken'),
                page: pageNumber,
                per_page: itemsPerPage
            });

            if (response.status === 'success') {
                setModuleData(response.data.data);
                setTotalItems(response.data.pagination.total);
                setCurrentItems(response.data.data);
            }
        } catch (err) {
            console.error('API Error:', err);
            setError('Error fetching data');
        } finally {
            setLoading(false);
        }
    };

    const fetchSearchResults = async (searchValue, pageNumber, itemsPerPage) => {
        setLoading(true);
        try {
            const response = await makeApiRequest('post', '/api/module/list', {
                token: localStorage.getItem('authToken'),
                page: pageNumber,
                per_page: itemsPerPage,
                search: searchValue
            });

            if (response.status === 'success') {
                setModuleData(response.data.data);
                setTotalItems(response.data.pagination.total);
                setCurrentItems(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching search results:', err);
            setError('Error fetching data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!searchTerm) {
            fetchPaginatedData(page + 1, rowsPerPage);
        }
    }, [page, rowsPerPage]);

    const [debouncedSearch] = useState(() =>
        debounce((searchValue, page, rowsPerPage) => {
            if (searchValue === '') {
                fetchPaginatedData(page + 1, rowsPerPage);
            } else {
                fetchSearchResults(searchValue, page + 1, rowsPerPage);
            }
        }, 300)
    );

    const handleSearchChange = (e) => {
        const searchValue = e.target.value;
        setSearchTerm(searchValue);
        setPage(0);
        debouncedSearch(searchValue, 0, rowsPerPage);
    };

    const handleEditClick = (index) => {
        setCurrentIndex(index);
        const moduleName = currentItems[index].module;
        setEditedModuleName(moduleName);
        setOriginalModuleName(moduleName);
        setCurrentModuleName(currentItems[index].module);
        setIsSaveDisabled(true);
        setOpen(true);
    };

    const handleModuleChange = (e) => {
        const updatedName = e.target.value;
        setEditedModuleName(updatedName);
        setIsSaveDisabled(updatedName === originalModuleName);
    };

    // const handleStatusChangeClick = (index, currentStatus) => {
    //     setCurrentIndex(index);
    //     setStatusToUpdate(currentStatus);
    //     setStatusChangeModalOpen(true);
    // };

    const handleSaveEdit = async () => {
        try {
            const response = await makeApiRequest('post', '/api/module/edit', {
                token: localStorage.getItem('authToken'),
                module_id: currentItems[currentIndex].module_id,
                updated_module: editedModuleName
            });

            if (response.status === 'success') {
                fetchPaginatedData(page + 1, rowsPerPage);
                setOpen(false);
                toast.success('Module updated successfully');
            } else {
                throw new Error('Failed to update chapter');
            }
        } catch (err) {
            console.error('API Error:', err);
            toast.error('Error updating module');
        }
    };



    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        if (searchTerm) {
            fetchSearchResults(searchTerm, newPage + 1, rowsPerPage);
        } else {
            fetchPaginatedData(newPage + 1, rowsPerPage);
        }
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);

        if (searchTerm) {
            fetchSearchResults(searchTerm, 1, newRowsPerPage);
        } else {
            fetchPaginatedData(1, newRowsPerPage);
        }
    };


    return (
        <Box sx={{ height: "auto", backgroundColor: "#ECEDFF" }}>
             <ToastContainer
                position="top-center"
                autoClose={2000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick={false}
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover={false}
                theme="light"
            />
            <Grid container sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Grid item xs={11}>
                    {/* Add chapter Section */}
                    <Box container sx={{ height: "80px", display: "flex", justifyContent: "start", alignItems: "center", backgroundColor: "#ECEDFF" }}>
                        <Typography variant="h4" sx={{
                            color: "#481E82", fontWeight: "500", fontFamily: 'Roboto'
                        }}> Add Module</Typography>
                    </Box>

                    <Grid item xs={12} sx={{
                        backgroundColor: 'white', boxShadow: " 0px 1px 2px rgba(0, 0, 0, 0.2);"
                        , padding: "20px", marginBottom: "20px", borderRadius: "5px"
                    }}>
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between"
                        }}>
                            <Grid container spacing={2} sx={{ justifyContent: "space-between" }}>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                    <Box sx={{
                                        display: "flex",
                                        gap: "20px",
                                        alignItems: "center",
                                        // width: { xs: "60%", sm: "40%" }
                                    }}>
                                        <TextField
                                            placeholder="Enter Module Name"
                                            value={newModuleName}
                                            onChange={(e) => setNewModuleName(e.target.value)}
                                            defaultValue="Small"
                                            size="small"
                                            sx={{
                                                width: "100%"
                                            }}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: "flex", justifyContent: "flex-end" }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleAddModule}
                                        size='medium'

                                    // sx={{ height: '50px' }}
                                    >
                                        Add Module
                                    </Button>
                                </Grid>
                            </Grid>

                        </Box>
                    </Grid>

                    {/* All chapter Section (Moved Below) */}
                    <Box container sx={{ height: "80px", display: "flex", justifyContent: "start", alignItems: "center", backgroundColor: "#ECEDFF" }}>
                        <Typography variant="h4" sx={{
                            color: "#481E82", fontWeight: "500", fontFamily: 'Roboto'
                        }}> All Modules</Typography>
                    </Box>

                    {/* Search Bar Section - Updated Padding */}
                    <Grid item xs={12} sx={{ backgroundColor: 'white', padding: "20px", borderRadius: "5px 5px 0 0" }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <TextField
                                        id="search-box"
                                        variant="outlined"
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        placeholder='Search Module'
                                        size='small'
                                        sx={{
                                            // width: { xs: "100%", sm: "40%" }
                                            width: "100%"
                                            // height: '50px',
                                            // '& .MuiOutlinedInput-root': {
                                            //     height: '50px',
                                            // }
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>

                    </Grid>
                    <Box>
                        {loading && (
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                                <CircularProgress size={40} thickness={4} sx={{ color: '#481E82' }} />
                            </Box>
                        )}

                        {/* {error && <Alert severity="error">{error}</Alert>} */}

                        {!loading && !error && currentItems.length === 0 && (
                            <Typography variant="h6" color="error" sx={{ textAlign: 'center' }}>
                                No data matching the search term.
                            </Typography>
                        )}

                        {!loading && !error && currentItems.length > 0 && (
                            <>
                                <TableContainer component={Paper} sx={{ borderRadius: "0px" }}>
                                    <Table sx={{ minWidth: 650, borderRadius: "0px" }} aria-label="chapter table">
                                        <TableHead sx={{ backgroundColor: '#ECEDFF', height: "60px", marginTop: "0px", margin: "0px", borderRadius: "0px" }}>
                                            <TableRow sx={{ borderRadius: "0px" }}>
                                                <StyledTableCell sx={{ width: '33.33%', color: 'black', fontSize: "14px", fontFamily: "Roboto" }}>
                                                    <strong>S/No.</strong>
                                                </StyledTableCell>
                                                <StyledTableCell sx={{ width: '33.33%', color: 'black', fontSize: "14px", fontFamily: "Roboto", textAlign: "start" }}>
                                                    <strong>Module Name</strong>
                                                </StyledTableCell>
                                                <StyledTableCell sx={{ width: '33.33%', color: 'black', fontSize: "14px", fontFamily: "Roboto" }}>
                                                    <strong>Action</strong>
                                                </StyledTableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {currentItems.map((item, index) => {
                                                const hasNoChapters = Number(item.chapters_count) === 0;
                                                return (
                                                    <StyledTableRow
                                                        key={index}
                                                        sx={hasNoChapters ? { backgroundColor: '#fff3e0' } : undefined}
                                                    >
                                                        <StyledTableCell sx={{ width: '33.33%', fontWeight: 500, fontSize: "14px" }}>
                                                            {index + 1 + page * rowsPerPage}
                                                        </StyledTableCell>
                                                        <StyledTableCell sx={{ width: '33.33%', fontWeight: 500, fontSize: "14px", color: "black", textAlign: "start" }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1 }}>
                                                                {item.module}
                                                                {hasNoChapters && (
                                                                    <Tooltip title="This module has no chapters yet">
                                                                        <Chip
                                                                            label="No Chapters"
                                                                            size="small"
                                                                            sx={{ backgroundColor: '#FFE0B2', color: '#E65100', fontWeight: 600 }}
                                                                        />
                                                                    </Tooltip>
                                                                )}
                                                            </Box>
                                                        </StyledTableCell>
                                                        <StyledTableCell sx={{ width: '33.33%' }}>
                                                            <Button variant="outlined" color="primary" size="small" onClick={() => handleEditClick(index)}>
                                                                <EditIcon sx={{ mr: 1 }} />  Edit
                                                            </Button>
                                                        </StyledTableCell>
                                                    </StyledTableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>


                                <TablePagination
                                    rowsPerPageOptions={[10, 20, 50]}
                                    component="div"
                                    count={totalItems}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </>
                        )}
                    </Box>
                </Grid>
            </Grid>
            <Modal
                open={open}
                onClose={() => setOpen(false)}
                aria-labelledby="modal-edit-title"
            >
                <Box sx={{ position: 'absolute', top: 100, left: '50%', transform: 'translateX(-50%)', width: 400, backgroundColor: 'white', borderRadius: 5, padding: 4, boxShadow: 24 }}>
                    <Typography variant="h6" id="modal-edit-title">
                        Edit Module
                    </Typography>
                    <TextField
                        fullWidth
                        label="Module Name"
                        variant="outlined"
                        value={editedModuleName}
                        onChange={handleModuleChange}
                        sx={{ marginBottom: 2, marginTop: "20px" }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveEdit}
                        sx={{ marginRight: 2 }}
                        disabled={isSaveDisabled}
                    >
                        Save
                    </Button>
                    <Button variant="outlined" color="secondary" onClick={() => setOpen(false)}>Cancel</Button>
                </Box>
            </Modal>
        </Box>
    );
};

export default Module;